<template>
    <div>
        <div class="flex flex-wrap justify-center gap-2">
            <div
                v-if="!!error"
                class="bg-theme-danger p-2 text-white border-sm"
            >
                {{ error }}
            </div>
            <div
                v-if="!!warning"
                class="bg-theme-warning mt-4 p-2 text-black border-sm"
            >
                {{ warning }}
            </div>
        </div>

        <div class="flex flex-wrap px-4 pb-4 mt-4">
            <div v-if="allowEditAlt" class="w-full">
                <div class="form-group">
                    <custom-input
                        v-model="alt"
                        type="text"
                        name="alt"
                        :disabled="isLoading"
                        label="Título de la imagen"
                        placeholder="Ej: Camiseta azul talla M"
                        aria-label="Título de la imagen"
                        autocomplete="off"
                        :error="
                            !validAlt && introValidators.alt
                                ? 'Ingresa un título sin símbolos raros, entre 5 y 64 carácteres.'
                                : ''
                        "
                        @blur="introValidators.alt = true"
                    >
                        <template #icon> 📷</template>
                    </custom-input>
                </div>
            </div>
            <div v-if="!defaultSize" class="w-full">
                <div class="form-group py-2">
                    <image-size-selector v-model="size" :enabled="!isLoading" />
                </div>
            </div>
        </div>
        <p v-if="!size" class="text-center text-theme-warning text-sm">
            Selecciona un tipo de imagen para habilitar la carga.
        </p>
        <p
            v-else-if="
                recommendedSizeLabel && !uploadFile && !pendingConfirmation
            "
            class="text-center text-theme-muted text-sm"
        >
            Tamaño recomendado: {{ recommendedSizeLabel }}
        </p>
        <p
            v-else-if="
                hasFreeSizeCondition && !uploadFile && !pendingConfirmation
            "
            class="text-center text-theme-muted text-sm"
        >
            Esta imagen se sube tal cual, sin un tamaño o proporción
            recomendada.
        </p>
        <div
            v-if="isLoading"
            class="flex flex-col items-center justify-center py-6"
        >
            <svg class="w-8 h-8 animate-spin" viewBox="0 0 24 24">
                <circle
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="#E5E7EB"
                    stroke-width="4"
                    fill="none"
                />
                <path
                    d="M22 12A10 10 0 0 0 12 2"
                    stroke="#D32F2F"
                    stroke-width="4"
                    stroke-linecap="round"
                    fill="none"
                />
            </svg>
            <span class="mt-3 text-sm text-theme-muted"
                >Subiendo imagen...</span
            >
        </div>
        <div
            v-else-if="pendingConfirmation"
            class="flex flex-col items-center gap-3 my-2"
        >
            <img
                loading="lazy"
                :src="previewUrl"
                alt="Imagen seleccionada"
                class="w-64"
            />
            <div class="flex gap-2">
                <app-button flat @click="clearSelectedFile">
                    Elegir otra imagen
                </app-button>
                <app-button color="warning" @click="confirmUploadAnyway">
                    Continuar de todas formas
                </app-button>
            </div>
        </div>
        <div
            v-else-if="!uploadFile"
            class="my-2 flex flex-col items-center gap-2"
        >
            <label
                class="w-64 flex flex-col items-center px-4 py-6 btn bg-theme-success cursor-pointer rounded-lg text-uppercase"
            >
                <img
                    src="@/assets/images/upload.svg"
                    height="20"
                    width="20"
                    alt="upload img"
                />
                <span class="mt-2 text-white leading-normal">
                    {{ updatedFile ? file.name : "Selecciona archivo" }}
                </span>
                <input
                    id="image-uploader"
                    ref="fileInput"
                    :disabled="isLoading || !size"
                    type="file"
                    accept=".jpg,.jpeg,.png,.webp"
                    class="hidden"
                    @input="uploadButton"
                />
            </label>
            <image-rights-notice v-if="!hideImageRightsNotice" />
        </div>
        <div v-else class="flex flex-wrap justify-center">
            <img
                loading="lazy"
                :src="previewUrl"
                alt="Imagen seleccionada"
                class="w-64"
            />
        </div>
    </div>
</template>

<script>
import { mapActions } from "vuex";

import { sizesImages } from "@/common/fields/sizes-images";
import {
    aspectErrorMessage,
    validateAspectRatio
} from "@/common/lib/image-dimensions";
import {
    SVG_NOT_ALLOWED_MESSAGE,
    isAllowedImageFile
} from "@/common/lib/image-files";
import { altRegex } from "@/common/lib/regex";

import CustomInput from "@/components/utils/CustomInput.vue";
import ImageRightsNotice from "@/components/utils/ImageRightsNotice.vue";
import ImageSizeSelector from "@/components/utils/ImageSizeSelector.vue";

export default {
    name: "ImageCreator",
    components: { CustomInput, ImageRightsNotice, ImageSizeSelector },
    props: {
        defaultSize: {
            default: () => null,
            type: String,
            required: false
        },
        defaultAlt: {
            default: () => "",
            type: String,
            required: false
        },
        allowEditAlt: {
            default: () => true,
            type: Boolean,
            required: false
        },
        useSharedEndpoints: {
            default: () => false,
            type: Boolean,
            required: false
        },
        hideImageRightsNotice: {
            type: Boolean,
            default: false
        }
    },
    emits: ["created", "loading"],
    data: () => ({
        id: null,
        alt: "",
        size: "",
        isLoading: false,
        uploadFile: false,
        file: { name: "", size: 0 },
        updatedFile: false,
        url: "",
        previewUrl: "",
        error: "",
        warning: "",
        pendingConfirmation: false,
        introValidators: { alt: false }
    }),
    computed: {
        validAlt() {
            return altRegex.test(this.alt);
        },
        sizeSpec() {
            return sizesImages.find((item) => item.size === this.size);
        },
        recommendedSizeLabel() {
            if (!this.sizeSpec || !this.sizeSpec.strict) return "";
            return `${this.sizeSpec.width} × ${this.sizeSpec.height}`;
        },
        hasFreeSizeCondition() {
            return !!this.sizeSpec && !this.sizeSpec.strict;
        }
    },
    mounted() {
        this.size = this.defaultSize || "squareImage";
        if (this.defaultAlt) {
            this.alt = this.defaultAlt;
        }
    },
    methods: {
        ...mapActions("images", [
            "getEndPointUploadImage",
            "getEndPointUploadSharedImage",
            "uploadImage",
            "createImage",
            "createSharedImage"
        ]),
        async uploadButton(event) {
            const auxFile = event.target.files || event.dataTransfer.files;
            if (!auxFile.length) return;
            this.file = auxFile[0];
            if (!isAllowedImageFile(this.file)) {
                this.error = SVG_NOT_ALLOWED_MESSAGE;
                this.clearSelectedFile();
                return;
            }
            this.previewUrl = URL.createObjectURL(this.file);
            const fileSize = this.file.size / 1048576;
            if (fileSize > 20) {
                this.error = `Archivo es demasiado pesado: ${fileSize} MB`;
                return;
            }
            this.error = "";
            this.warning = "";
            this.updatedFile = true;
            if (this.sizeSpec) {
                const aspect = await validateAspectRatio(
                    this.file,
                    this.sizeSpec
                );
                if (!aspect.valid) {
                    this.warning = aspectErrorMessage(aspect);
                    this.pendingConfirmation = true;
                    return;
                }
            }
            await this.startUpload();
        },
        confirmUploadAnyway() {
            this.startUpload();
        },
        async startUpload() {
            this.pendingConfirmation = false;
            this.isLoading = true;
            this.$emit("loading", true);
            try {
                const getEndPointUpload = this.useSharedEndpoints
                    ? this.getEndPointUploadSharedImage
                    : this.getEndPointUploadImage;
                const finalizeUpload = this.useSharedEndpoints
                    ? this.createSharedImage
                    : this.createImage;
                const {
                    id,
                    url: uploadUrl,
                    urlSigned
                } = await getEndPointUpload({
                    fileName: this.file.name,
                    fileSize: this.file.size,
                    alt: this.alt,
                    size: this.size
                });
                this.id = id;
                this.url = uploadUrl;
                await this.uploadImage({
                    url: urlSigned,
                    file: this.file
                });
                this.uploadFile = true;

                // finalizeUpload siempre cierra el cargue: el backend completa
                // el alt faltante y marca la imagen como cargada con éxito
                const { alt, size, url } = await finalizeUpload({
                    id,
                    url: uploadUrl,
                    alt: this.alt,
                    size: this.size
                });
                this.url = url;
                this.$emit("created", {
                    alt,
                    id,
                    previewUrl: this.previewUrl,
                    size,
                    url
                });
                this.resetAfterCreate();
                this.$emit("loading", false);
                this.isLoading = false;
            } catch (error) {
                this.$emit("loading", false);
                this.isLoading = false;
                this.error = error.message;
                this.uploadFile = false;
                this.updatedFile = false;
                this.file = { name: "", size: 0 };
            }
        },
        clearSelectedFile() {
            if (this.previewUrl) {
                URL.revokeObjectURL(this.previewUrl);
            }
            this.previewUrl = "";
            this.updatedFile = false;
            this.warning = "";
            this.pendingConfirmation = false;
            this.file = { name: "", size: 0 };
            if (this.$refs.fileInput) {
                this.$refs.fileInput.value = null;
            }
        },
        resetAfterCreate() {
            this.uploadFile = false;
            this.updatedFile = false;
            this.introValidators.alt = false;
            this.file = { name: "", size: 0 };
            this.alt = this.defaultAlt || "";
            this.warning = "";
            if (this.$refs.fileInput) {
                this.$refs.fileInput.value = null;
            }
        }
    }
};
</script>
